import { useCallback, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { css } from "../../../../../../styled-system/css";
import { Box, Grid } from "../../../../../../styled-system/jsx";
import { PageContainer } from "../../../../../components/core/basic/PageContainer";
import { DevErrorDisplay } from "../../../../../components/core/blocks/DevErrorDisplay";
import {
  periodEndingToday,
  useRefreshUserActivity,
  useUserActivityReport,
} from "../../../../../modules/user_activity/api/useUserActivity";
import { ActivityTrendChart, WorkSplitChart } from "../../../../../modules/user_activity/components/ActivityCharts";
import { PeopleTable } from "../../../../../modules/user_activity/components/PeopleTable";
import { PersonModal } from "../../../../../modules/user_activity/components/PersonModal";
import {
  KpiTiles,
  PERIOD_OPTIONS,
  type PeriodOption,
  ReportHeader,
  SummaryCallout,
} from "../../../../../modules/user_activity/components/ReportOverview";
import {
  GeneratingReport,
  JobFailedNotice,
  UpdatingBanner,
} from "../../../../../modules/user_activity/components/ReportLoading";
import { FeatureAdoption, WorkingHoursHeatmap } from "../../../../../modules/user_activity/components/UsagePatterns";
import { type Audience, summarize, workSplit } from "../../../../../modules/user_activity/model";

const AUDIENCES: Audience[] = ["team", "trellis", "everyone"];

function MeasurementNote() {
  const { t } = useTranslation(["user_activity"]);
  return (
    <details className={css({ color: "text.secondary", fontSize: "small" })}>
      <summary className={css({ cursor: "pointer", fontWeight: "600" })}>
        {t(($) => $.user_activity["How this report is measured"])}
      </summary>
      <ul className={css({ marginTop: "small", paddingLeft: "large", lineHeight: "1.7" })}>
        <li>
          {t(
            ($) =>
              $.user_activity[
                "Page views count visits to the dashboard and to campaign, product, category and pricing plan pages. Other pages are not recorded."
              ],
          )}
        </li>
        <li>
          {t(
            ($) =>
              $.user_activity[
                "Changes count each campaign, keyword, target or rule a person changed, once per hour. Changes made through bulk uploads and the AI assistant count for the person who made them."
              ],
          )}
        </li>
        <li>
          {t(
            ($) =>
              $.user_activity[
                "Changes Trellis automation makes on its own, like bid updates, are not included: this report is about people."
              ],
          )}
        </li>
        <li>
          {t(
            ($) =>
              $.user_activity[
                "Days follow UTC; the working-hours chart uses your own time zone. Last seen is the later of a person's last login and their last recorded action."
              ],
          )}
        </li>
      </ul>
    </details>
  );
}

function UserActivityPage() {
  const { t } = useTranslation(["user_activity"]);
  const { org_id } = Route.useParams();
  const orgId = Number(org_id);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const days: PeriodOption = PERIOD_OPTIONS.find((option) => option === search.days) ?? "30";
  const audience: Audience = AUDIENCES.find((option) => option === search.audience) ?? "team";
  const openPersonId = search.person ? Number(search.person) : null;

  const period = useMemo(() => periodEndingToday(Number(days)), [days]);
  const report = useUserActivityReport(orgId, period);
  const refresh = useRefreshUserActivity(orgId, period);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const shown = report.report;
  const summary = useMemo(() => (shown ? summarize(shown, audience) : null), [shown, audience]);
  const split = useMemo(() => (shown ? workSplit(shown) : []), [shown]);
  const everyone = useMemo(() => (shown ? summarize(shown, "everyone") : null), [shown]);
  const shownPeriod = useMemo(
    () => (shown ? { start: shown.period.start, end: shown.period.end } : period),
    [shown, period],
  );
  const isUpdating = report.isRunning || isRefreshing;

  const setSearch = useCallback(
    (changes: Record<string, string | undefined>) =>
      void navigate({ search: (previous) => ({ ...previous, ...changes }), replace: true }),
    [navigate],
  );

  const onRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <PageContainer css={{ gap: "large", padding: { base: "medium", md: "large" }, backgroundColor: "#faf9fc" }}>
      <ReportHeader
        orgName={shown?.org.name}
        days={days}
        onDaysChange={(value) => setSearch({ days: value })}
        audience={audience}
        onAudienceChange={(value) => setSearch({ audience: value })}
        generatedAt={shown?.generated_at}
        isFetching={isUpdating}
        onRefresh={() => void onRefresh()}
      />

      {!shown || !summary || !everyone ?
        report.isFailed ?
          <>
            <JobFailedNotice compact={false} onRetry={() => void onRefresh()} />
            {report.error !== null && (
              <DevErrorDisplay
                error={report.error}
                userMessage={t(($) => $.user_activity["Couldn't load this report."])}
              />
            )}
          </>
        : <GeneratingReport days={Number(days)} startedAt={report.startedAt} />
      : <Box display="flex" flexDirection="column" gap="large">
          {report.isStale && report.isFailed && <JobFailedNotice compact onRetry={() => void onRefresh()} />}
          {report.isStale && isUpdating && <UpdatingBanner days={Number(days)} startedAt={report.startedAt} />}
          <Box
            display="flex"
            flexDirection="column"
            gap="large"
            transition="opacity 0.2s"
            style={{ opacity: report.isStale && isUpdating ? 0.55 : 1 }}
          >
            <SummaryCallout summary={summary} audience={audience} unavailableSources={shown.unavailable_sources} />
            <KpiTiles summary={summary} audience={audience} />
            <ActivityTrendChart summary={summary} audience={audience} />
            <Grid gridTemplateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap="large" alignItems="start">
              <FeatureAdoption summary={summary} />
              <WorkSplitChart split={split} />
            </Grid>
            <WorkingHoursHeatmap summary={summary} />
            <PeopleTable
              people={summary.people}
              days={summary.days.length}
              onOpenPerson={(userId) => setSearch({ person: String(userId) })}
            />
            <MeasurementNote />
            <PersonModal
              stats={everyone.people.find((stats) => stats.person.user_id === openPersonId)}
              report={shown}
              period={shownPeriod}
              onClose={() => setSearch({ person: undefined })}
            />
          </Box>
        </Box>
      }
    </PageContainer>
  );
}

export const Route = createFileRoute("/_authenticated/user/staff/user-activity/$org_id")({
  component: UserActivityPage,
});
